import React, { useState, useEffect } from 'react';
import './CareerForm.css';

// Import necessary components from the components directory
import { Heading, PageTitle } from '../../../components';

import { FaUser, FaEnvelope, FaPhone, FaGraduationCap, FaBriefcase, FaCommentDots, FaAngleDown } from 'react-icons/fa';
import { FaLocationDot, FaPersonHiking } from 'react-icons/fa6';
import axios from "axios";

// API endpoint for submitting the career form
const API_PATH = 'https://campfire-react.asdesignsgalaxy.com/api/phpmailer/career.php';

// Regex patterns for email and phone number validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;



const CareerForm = () => {

  // State to manage form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    position: '',
    location: '',
    education: '',
    experience: '',
    message: ''
  });

  const [mailSent, setMailSent] = useState(false); // State to track if the email was sent successfully
  const [error, setError] = useState(''); // State to store error messages
  const [isSubmitting, setIsSubmitting] = useState(false); // State to track if the form is being submitted

  // Effect to reset the mailSent state after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  /**
   * Handles form submission.
   * Validates form data and sends it to the server.
   */
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Full name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.position) validationErrors.push('Position is required');
      if (!formData.location.trim()) validationErrors.push('Location is required');
      if (!formData.education.trim()) validationErrors.push('Highest qualification is required');
      if (!formData.experience || formData.experience < 0) validationErrors.push('Valid years of experience is required');
      if (!formData.message.trim()) validationErrors.push('Message is required');

      // If there are validation errors, throw them
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Send form data to the server
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle server response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Application submission failed');
      }

      // Reset form and show success message
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        position: '',
        location: '',
        education: '',
        experience: '',
        message: ''
      });
    } catch (err) {
      // Handle errors
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles input changes and updates the form data state.
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
<>
  <PageTitle title={'Career Form'} page={'Career Form'} />

  <section className="custom-form career-form linear-bg">
    <div className="box-container">
      <div className="content">

        {/* Career Form */}
        <form className="dark-form" onSubmit={handleSubmitForm}>

          {/* Form Heading */}
          <Heading title={"Career Application"} subTitle={"Join Our Adventure Team"} linear sideHeading />

          <div className="input-box">

            {/* Name Input Field */}
            <div className="input-field">
              <label htmlFor="name"><FaUser /></label>
              <input
                type="text"
                name="name"
                className="box"
                id="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Email Input Field */}
            <div className="input-field">
              <label htmlFor="email"><FaEnvelope /></label>
              <input
                type="email"
                name="email"
                className="box"
                id="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="input-box">

            {/* Phone Number Input Field */}
            <div className="input-field">
              <label htmlFor="number"><FaPhone /></label>
              <input
                type="number"
                name="number"
                className="box"
                id="number"
                placeholder="Your Phone Number"
                value={formData.number}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Position Applied For Select Field */}
            <div className="select">
              <div className="select-container">
                <label htmlFor="position"><FaPersonHiking /></label>
                <select
                  className="select-box"
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Position</option>
                  <option value="Hiking Guide">Hiking Guide</option>
                  <option value="Camping Instructor">Camping Instructor</option>
                  <option value="Trekking Leader">Trekking Leader</option>
                  <option value="Wilderness First Responder">Wilderness First Responder</option>
                  <option value="Outdoor Survival Expert">Outdoor Survival Expert</option>
                  <option value="Other">Other</option>
                </select>
                <div className="icon-container"><FaAngleDown className="icon" /></div>
              </div>
            </div>
          </div>

          {/* Location Input Field */}
          <div className="input-box">
            <div className="input-field">
              <label htmlFor="location"><FaLocationDot /></label>
              <input
                type="text"
                name="location"
                className="box"
                id="location"
                placeholder="Your Location"
                value={formData.location}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="input-box">

            {/* Education Input Field */}
            <div className="input-field">
              <label htmlFor="education"><FaGraduationCap /></label>
              <input
                type="text"
                name="education"
                className="box"
                id="education"
                placeholder="Outdoor Certifications"
                value={formData.education}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>

            {/* Years of Experience Input Field */}
            <div className="input-field">
              <label htmlFor="experience"><FaBriefcase /></label>
              <input
                type="number"
                name="experience"
                className="box"
                id="experience"
                placeholder="Years of Outdoor Experience"
                value={formData.experience}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Message Textarea */}
          <div className="input-field text-area">
            <label htmlFor="message"><FaCommentDots /></label>
            <textarea
              cols="30"
              rows="6"
              name="message"
              className="box"
              id="message"
              placeholder="Why do you want to join our outdoor team? Mention your experience with hiking, camping, wilderness first aid, navigation skills, and any relevant outdoor certifications."
              value={formData.message}
              onChange={handleInputChange}
              required
              disabled={isSubmitting}
            ></textarea>
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            className="btn" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>

          {/* Error and Success Messages */}
          {error && (
            <div className="alert error">
              {error.split(' | ').map((msg, i) => (
                <span key={i}>{msg}</span>
              ))}
            </div>
          )}
          {mailSent && <div className="alert success"><span>Application Submitted Successfully</span></div>}
        </form>
      </div>
    </div>
  </section>
</>
  );
}

export default CareerForm;